<template>
  <div :class="controlClasses">
    <label :for="uid" :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <div class="control-row">
        <button
          @click="decrease"
          class="control-btn decrease"
          type="button"
          v-if="showButtons"
        >
          <VIcon material="remove" />
        </button>

        <div class="mds-form-control-icon" v-if="hasIconSlot">
          <!-- @slot Слот иконки -->
          <slot name="icon" />
        </div>

        <template v-if="isDisabledHintVisible">
          <button class="disable-hint-container" :id="`hint-${uid}`">
            <VIcon theme="outlined" material="question_mark" />
          </button>

          <VTooltip :target="`#hint-${uid}`">
            <ul>
              <li v-for="(reason, index) in disabledReasons" :key="index">
                {{ reason }}
              </li>
            </ul>
          </VTooltip>
        </template>

        <input
          :class="{ 'with-suffix': hasHelperTextSlot && helperTextPosition === 'right' }"
          :disabled="disabled"
          :id="uid"
          :placeholder="placeholder"
          :value="modelValue"
          type="number"
          @input="onInput"
          @blur="onBlur"
          @focus="onFocus"
        />

        <span class="unit" v-if="unit">{{ unit }}</span>

        <button v-if="showClear" @click="onClear" class="clear" type="button">
          <VIcon material="cancel" />
        </button>

        <!-- helper-text как суффикс справа — часть control-row -->
        <div class="helper-text helper-text--right" v-if="hasHelperTextSlot && helperTextPosition === 'right'">
          <slot name="helper-text" />
        </div>

        <button
          @click="increase"
          class="control-btn increase"
          type="button"
          v-if="showButtons"
        >
          <VIcon material="add" />
        </button>
      </div>

      <div class="messages-row" v-if="isErrorsVisible">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <!-- helper-text снизу (по умолчанию) -->
        <div class="helper-text helper-text--bottom" v-if="hasHelperTextSlot && helperTextPosition === 'bottom'">
          <slot name="helper-text" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./VInputNumber.scss" />

<script lang="ts">
import Utils        from "@/core/utils/Utils";
import VBaseControl from "@/core/services/VBaseControl";

import VIcon    from "@/lib/components/VIcon/VIcon.vue";
import VTooltip from "@/lib/components/VTooltip/VTooltip.vue";
import Constants from "@/core/misc/Constants";
import type Unit from "@/domains/Units/Unit";

export default defineComponent({
  name: "VInputNumber",

  components: { VTooltip, VIcon },

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** v-model */
    modelValue: { type: Number,  default: null },
    /** Кнопка очистки */
    clearable:  { type: Boolean, default: null },
    /** Валидация по строке/цифре формата "10.3". Приоритет ниже, чем у min, max, precision. */
    range:      { type: [Number, String], default: null },
    /** Шаг изменения значения по кнопкам, кратность. */
    step:       { type: Number,  default: 1 },
    /** Показывать кнопки увеличения/уменьшения значения. */
    showButtons: { type: Boolean, default: false },
    /** Количество знаков после запятой. */
    precision: {
      type:      Number,
      default:   2,
      validator: (value: number) => value >= 0,
    },
    /** Выравнивание текста */
    textAlign: {
      type:      String,
      default:   null,
      validator: (value: string) => Constants.TEXT_ALIGNS.includes(value),
    },
    /**
     * Позиция вспомогательного текста.
     * 'bottom' — под инпутом (по умолчанию),
     * 'right'  — справа от инпута, на одном уровне с ним
     */
    helperTextPosition: {
      type:      String,
      default:   'bottom',
      validator: (value: string) => ['bottom', 'right'].includes(value),
    },
    /** Единица измерения справа от значения. Принимает строку ("кг", "шт", "$") или объект Unit. */
    unit:        { type: [String, Object] as PropType<string | Unit>, default: null },
  },

  emits: ["update:modelValue", "blur", "focus", "input"],

  computed: {
    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.clearable === true)   classes.push("clearable");
      if (this.showButtons === true) classes.push("show-buttons");
      // Корневые классы базовой разметки этому компоненту нужны явно,
      // т.к. шаблон выше использует :class="controlClasses" без статичных классов.
      classes.push("mds-input-number");
      classes.push("mds-form-control");
      return classes;
    },

    showClear(): boolean {
      return this.clearable === true
        && this.modelValue !== null
        && this.disabled === false;
    },

    /** labelStyles переопределяем — у инпута исторически width, а не flex. */
    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      if (this.textAlign  !== null) result["text-align"] = this.textAlign;
      return result;
    },

    hasIconSlot(): boolean {
      return this.$slots.icon !== undefined;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },

    getValidateRules() {
      return this.parseRangeRules();
    },

    getPrecision(): number {
      return this.parseRangeRules().precision;
    },
  },

  watch: {
    modelValue(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.debounceValidation();
      }
    },
  },

  methods: {
    /**
     * Парсит проп range='10.3' в { min, max, precision }, накладывая обычные
     * пропы min/max поверх (они имеют приоритет).
     *
     * Формат range:
     *  - "10.3" или 10.3 → max = 9999999 (7 цифр), precision = 3
     *  - "-10.3" → симметричный диапазон [-9999999, 9999999]
     *  - "10"   → max по разрядности целых, без дробной части
     */
    parseRangeRules() {
      const { min, max, precision, range } = this;
      const result: { min: number | null; max: number | null; precision: number } = {
        min:       null,
        max:       null,
        precision,
      };

      let rangeMax:       number | null = null;
      let rangePrecision: number | null = null;
      let haveMinus = false;

      if (Utils.number.isNumber(range)) {
        let rangeStr = String(range);
        if (rangeStr[0] === "-") {
          haveMinus = true;
          rangeStr  = rangeStr.substring(1);
        }
        const parts                = rangeStr.split(".");
        const rangeMaxNumber       = parseInt(parts[0], 10);
        const rangePrecisionNumber = parts[1] ? parseInt(parts[1], 10) : 0;

        if (!isNaN(rangeMaxNumber) && rangeMaxNumber - rangePrecisionNumber >= 0) {
          rangeMax = rangeMaxNumber - rangePrecisionNumber;
        }
        if (!isNaN(rangePrecisionNumber)) {
          rangePrecision = rangePrecisionNumber;
        }
      }

      if (typeof range === "string") {
        let rangeStr = range;
        if (rangeStr[0] === "-") {
          haveMinus = true;
          rangeStr  = rangeStr.substring(1);
        }
        const [rangeMaxString, rangePrecisionString] = rangeStr.split(".");

        if (typeof rangeMaxString === "string" && rangeMaxString.length > 0) {
          rangeMax = rangeMaxString.length;
        }
        if (typeof rangePrecisionString === "string" && rangePrecisionString.length > 0) {
          rangePrecision = rangePrecisionString.length;
        }
      }

      if (Utils.number.isNumber(rangePrecision)) {
        result.precision = rangePrecision as number;
      }
      if (Utils.number.isNumber(rangeMax)) {
        result.max = 10 ** (rangeMax as number) - 1;
        result.min = haveMinus ? (10 ** (rangeMax as number) - 1) * -1 : 0;
      }
      if (Utils.number.isNumber(min)) result.min = min;
      if (Utils.number.isNumber(max)) result.max = max;

      return result;
    },

    onInput(event) {
      const target        = event.target as HTMLInputElement;
      const withPrecision = this._toPrecision(target.value);
      target.value        = String(withPrecision === null ? "" : withPrecision);

      if (this.validation.isDirty === false && this.modelValue !== withPrecision) {
        this.validation.markDirty();
      }

      this.$emit("update:modelValue", withPrecision);
      this.$emit("input", withPrecision);
    },

    async onBlur(event) {
      if (typeof this.debounceValidation.cancel === "function") {
        this.debounceValidation.cancel();
      }
      await this.validate();
      const target        = event.target as HTMLInputElement;
      const withPrecision = this._toPrecision(target.value);
      target.value        = String(withPrecision === null ? "" : withPrecision);
      this.$emit("blur", withPrecision);
    },

    onFocus(event) {
      this.validation.markTouched();
      this.$emit("focus", event.target);
    },

    onClear() {
      this.$emit("update:modelValue", null);
      this.$emit("input", null);
      this.$emit("blur", null);
      nextTick(() => this.validate());
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      await this.validation.validate(this.modelValue);
    },

    decrease(): void {
      let result = this.modelValue !== null ? this.modelValue : 0;

      result = Utils.number.round10(result - this.step, this.getValidateRules.precision);

      if (this.getValidateRules.min !== null && result <= this.getValidateRules.min) {
        result = this.getValidateRules.min;
      }
      this.$emit("update:modelValue", result);
      this.$emit("input", result);
      nextTick(() => this.validate());
    },

    increase(): void {
      let result = this.modelValue !== null ? this.modelValue : 0;

      result = Utils.number.round10(result + this.step, this.getValidateRules.precision);

      if (this.getValidateRules.max !== null && result >= this.getValidateRules.max) {
        result = this.getValidateRules.max;
      }
      this.$emit("update:modelValue", result);
      this.$emit("input", result);
      nextTick(() => this.validate());
    },

    _convertValue(value: number | string | null): number | null {
      if (value === null || value === undefined || value === "") {
        return null;
      }
      switch (typeof value) {
        case "number":
          return value;
        case "string": {
          if (value.length === 0) return null;
          const parsed = parseFloat(value);
          return isNaN(parsed) ? null : parsed;
        }
        default:
          return null;
      }
    },

    _toPrecision(number: number | string | null): number | null {
      const temp = this._convertValue(number);
      if (temp === null) return null;
      if (this.getPrecision === 0) return Math.round(temp);
      const size = Math.pow(10, this.getPrecision);
      return Math.round(temp * size) / size;
    },
  },
});
</script>
